import { usePatientEpisodesOfCareOverview } from '@ctw/shared/api/fhir/episode-of-care';
import type { EpisodeOfCareModel } from '@ctw/shared/api/fhir/models/episode-of-care';
import { withOverviewCardErrorBoundary } from '@ctw/shared/content/overview/with-overview-card-error-boundary';
import { ResourceTitleColumn } from '@ctw/shared/content/resource/resource-title-column';
import { datePredicate } from '@ctw/shared/content/resource/view-date-range';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { applySorts } from '@ctw/shared/utils/sort';
import { tw } from '@ctw/shared/utils/tailwind';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
import { faCircleH } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { compact } from 'lodash-es';
import { applyFilters, type OverviewFilter } from '../overview/filters';
import type { SpecificResourceOverviewCardProps } from '../overview/resource-overview-card';
import { ResourceOverviewCard } from '../overview/resource-overview-card';

const TITLE = 'ED & IP Visits';

export const EpisodeOfCareOverviewCard = withOverviewCardErrorBoundary({
  cardTitle: TITLE,
  boundaryName: 'EpisodeOfCareOverviewCard',
  Component: ({ onSeeAllResources }: SpecificResourceOverviewCardProps) => {
    const { openResourceDrawer } = useResourceDrawer({ enableReadActions: true });
    const episodesOfCareQuery = usePatientEpisodesOfCareOverview();
    const sortedData = applySorts(episodesOfCareQuery.data ?? [], [
      { key: 'periodStart', dir: 'desc', isDate: true }, // Newest first.
    ]);
    const filteredData = applyFilters(sortedData, getEpisodeOfCareOverviewFilters());

    const loading = episodesOfCareQuery.isLoading;
    const hasData = filteredData.length > 0;

    return (
      <ResourceOverviewCard
        headerIcon={faCircleH}
        title={TITLE}
        data={filteredData}
        emptyState={
          filteredData.length === 0
            ? "We didn't find any emergency or inpatient records for this patient."
            : 'No emergency or inpatient visits found within the past year.'
        }
        onRowClick={({ telemetry, record }) => {
          openResourceDrawer({ telemetry, model: record });
        }}
        footerCTA={
          loading || hasData
            ? {
                label: 'All Encounters',
                onClick: onSeeAllResources,
              }
            : undefined
        }
        loading={loading}
        helpText="Emergency and inpatient visits from the past year."
        testId="episodes-of-care-overview"
        renderResource={(episodeOfCare: EpisodeOfCareModel) => {
          const documents = episodeOfCare.dischargeBinaryIds;

          return (
            <ResourceTitleColumn
              badges={compact([
                episodeOfCare.isReadmission && {
                  variant: 'info',
                  content: 'Readmission',
                },
                episodeOfCare.isNonelectiveAdmission && {
                  variant: 'info',
                  content: 'Nonelective',
                },
                episodeOfCare.isBehavioralHealth && {
                  variant: 'info',
                  content: 'Behavioral',
                },
              ])}
              title={episodeOfCare.title}
              subTitle={
                <>
                  <div className={tw`-mt-1 text-content-main/75 text-xs`}>
                    {episodeOfCare.subtitle}
                  </div>
                  <div className={tw`space-y-0.5`}>
                    {episodeOfCare.lastLocation && (
                      <div className={tw`font-normal`}>{episodeOfCare.lastLocation}</div>
                    )}
                    {documents.length > 0 ? (
                      <div className={tw`flex items-center space-x-1.5 pt-1 font-normal text-sm`}>
                        <FontAwesomeIcon className={tw`w-4 text-content-icon`} icon={faFileLines} />
                        <div>
                          {documents.length} {documents.length === 1 ? 'document' : 'documents'}
                        </div>
                      </div>
                    ) : undefined}
                  </div>
                </>
              }
            />
          );
        }}
      />
    );
  },
});

const getEpisodeOfCareOverviewFilters = (): Array<OverviewFilter<EpisodeOfCareModel>> => [
  {
    key: 'emergency',
    label: 'Filter for emergency episodes',
    description: 'Is an emergency or a hospitalization.',
    predicate: (episodeOfCare: EpisodeOfCareModel) =>
      ['EMER', 'IMP', 'OBSENC', 'PRENC'].includes(episodeOfCare.classCode),
  },
  {
    key: 'recent',
    label: 'Filter for recent episodes',
    description: 'Began within the past year.',
    predicate: (episodeOfCare: EpisodeOfCareModel) =>
      datePredicate(episodeOfCare, 'periodStart', 365, false),
  },
];
